import { useMutation, useQueryClient } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { taskQueryKeys } from '@omnea-app/fe-tasks/queryKeys'
import { RequestDetail, UpdateRequestPropertiesInput } from '@omnea-app/requests/requestsFrontendClient'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { RequestDetailQueryKey } from '../../useRequestDetail'

type UpdateRequestPropertiesArgs = UpdateRequestPropertiesInput & {
  requestId: string
}

export const useUpdateRequestProperties = () => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('requests')

  return useMutation({
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [RequestDetailQueryKey, variables.requestId] })

      const previousDetail = queryClient.getQueryData([RequestDetailQueryKey, variables.requestId])

      queryClient.setQueryData([RequestDetailQueryKey, variables.requestId], (old: RequestDetail | undefined) => {
        if (!old) return old
        return {
          ...old,
          rawData: {
            ...old.rawData,
            ...(variables.name !== undefined && { name: variables.name }),
          },
        }
      })

      return { previousDetail }
    },
    onError: (_error, variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData([RequestDetailQueryKey, variables.requestId], context.previousDetail)
      }
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({ queryKey: [RequestDetailQueryKey, variables.requestId] })
    },
    onSuccess: async (_data, variables) => {
      const requestId = variables?.requestId

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: taskQueryKeys.all }),
        queryClient.invalidateQueries({
          queryKey: [RequestDetailQueryKey, requestId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['dashboard-requests'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['dashboard-requests-stats'],
        }),
      ])
    },
    mutationFn: async ({ requestId, ...requestProperties }: UpdateRequestPropertiesArgs) => {
      const noPropertiesSpecified =
        Object.values(requestProperties).every((value) => value === undefined) || isEmpty(requestProperties)

      if (noPropertiesSpecified) {
        console.error('No properties specified when trying to update request')
        throw new Error('No properties specified when trying to update request')
      }

      const token = await getToken()
      const requestsClient = new RequestsHttpClient({ authorization: token })

      const { data, error } = await requestsClient.POST('/{id}/update-request-properties', {
        params: { path: { id: requestId } },
        body: requestProperties,
      })

      if (error && error.type === 'EngagementAlreadyCompleteError') {
        enqueueSnackbar(t('profile.errors.error-updating-budget'), { variant: 'error' })
        throw new Error('Error updating request properties user')
      }

      if (isNil(data) || error) {
        enqueueSnackbar(t('profile.errors.generic'), { variant: 'error' })
        throw new Error('Error updating request properties')
      }

      return data
    },
  })
}
